import motion from "../../motion-tokens.json" with { type: "json" };
import { easeOutCubic, formatFigure, motionAllowed, parseFigure } from "./motion.js";

/**
 * x-figure, on a picker card's figure (ActionsView; D58 of documentation/pr-plans/insights-s0-plan-and-seats.md,
 * Ali, 9 Oct). The figures the page was drawn with are on screen from the first paint and stay put; a
 * figure that arrives later counts in over the `view` token on an ease-out cubic, while it is on screen:
 * - one drawn into the picker after the page started (a cold card filled in) counts up from 0;
 * - one drawn again with a new value (the cards counted again) moves from the value it showed to the
 *   new one, from wherever a count still running had got to;
 * - one drawn again unchanged, or shown again by Back, does not move.
 * Its last frame puts the text back exactly as the server drew it, and under reduced motion it is only
 * ever the server's text. Apart from motion.js, whose x-count-up counts a screen's figures up once per
 * page load, and tested apart (InsightsFigureMotionTest).
 */

// Whether Alpine has started the page: a figure it starts with was painted by the page itself, and
// counting it up would first take away a number already on screen.
let started = false;

export function markStarted() {
    started = true;
}

if (typeof document !== "undefined") {
    document.addEventListener("alpine:initialized", markStarted, { once: true });
}

/**
 * What a count from `from` (null: from 0) to `to` shows at `progress`, from 0 to 1, printed as `to` is:
 * its decimals, its commas and the text around its number, and `to` itself at the end. Null when either
 * text is not one number, or both are the same number: nothing then moves.
 *
 * @returns {string|null}
 */
export function figureAt(from, to, progress) {
    const target = parseFigure(to);
    const origin = from === null ? (target === null ? null : { ...target, value: 0 }) : parseFigure(from);

    if (target === null || origin === null || origin.value === target.value) {
        return null;
    }

    if (progress >= 1) {
        return to;
    }

    return formatFigure(target, origin.value + (target.value - origin.value) * easeOutCubic(Math.max(0, progress)));
}

export function FigureDirective(el, _directive, { cleanup }) {
    // The text as the server last drew it, what the running count last wrote (which the watch skips),
    // and the count's next frame.
    let drawn = el.textContent;
    let written = null;
    let frame = null;

    const stop = () => {
        cancelAnimationFrame(frame);
        frame = null;
        written = null;
    };

    // From `from` to the figure as drawn now, when there is a number to move and it is on screen.
    const count = (from) => {
        stop();
        const to = drawn;

        if (!motionAllowed() || el.offsetParent === null || figureAt(from, to, 0) === null) {
            return;
        }

        let start = null;
        const step = (now) => {
            start ??= now;
            const progress = Math.min(1, (now - start) / motion.duration.view);
            written = figureAt(from, to, progress);
            el.textContent = written;
            frame = progress < 1 ? requestAnimationFrame(step) : null;
        };

        written = figureAt(from, to, 0);
        el.textContent = written;
        frame = requestAnimationFrame(step);
    };

    if (started) {
        count(null);
    }

    // Livewire's morph writes a figure drawn again into this element, in place.
    const watch = new MutationObserver(() => {
        const text = el.textContent;

        if (text === written) {
            return;
        }

        const shown = written ?? drawn;
        const isChanged = text !== drawn;
        drawn = text;

        if (isChanged) {
            count(shown);
        } else {
            // The same figure written back over a count: it stands as drawn.
            stop();
        }
    });

    watch.observe(el, { childList: true, characterData: true, subtree: true });

    cleanup(() => {
        watch.disconnect();
        stop();
    });
}
